import { environment } from '../../environments/environment';
import { exists } from '../functions/exists';

/**
 * BudSense auth and source headers may only reach a configured BudSense API origin. Every other request (S3 presigned
 * urls, CDN assets, the app's own static files, third parties) must pass through the interceptors untouched: custom
 * headers there leak credentials off-API and force a CORS preflight on every cross-origin GET.
 *
 * Keep this file identical in budsense-client, budsense-admin-client, budsense-high-guide and budsense-client-display.
 */
export class ApiOriginUtils {
  // Derived from every environment.*ServiceUrl so a new service, or one hosted on another origin, is covered
  private static readonly apiOrigins = new Set<string>(
    Object.entries(environment)
      .filter(([key, value]) => key.endsWith('ServiceUrl') && typeof value === 'string')
      .map(([, value]) => ApiOriginUtils.originOf(value as string))
      .filter(exists)
  );

  // Relative urls resolve against the app's own origin, which is never an API origin
  static originOf(url: string): string {
    return ApiOriginUtils.parse(url)?.origin;
  }

  static pathnameOf(url: string): string {
    return ApiOriginUtils.parse(url)?.pathname;
  }

  static isBudSenseApiUrl(url: string): boolean {
    return ApiOriginUtils.apiOrigins.has(ApiOriginUtils.originOf(url));
  }

  private static parse(url: string): URL {
    if (!url) return null;
    try {
      return new URL(url, window.location.origin);
    } catch {
      return null;
    }
  }
}
